export type PlanType = 'starter' | 'growth' | 'enterprise' | 'custom';
export type SubscriptionStatus = 'active' | 'trial' | 'suspended' | 'cancelled' | 'expired';

export interface Tenant {
  id: number;
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  address?: string;
  companyStatus: string;
  planName: string;
  planSlug: PlanType;
  basePrice: number;
  subscriptionStatus: SubscriptionStatus;
  trialEndsAt?: string;
  nextBillingDate?: string;
  credits: number;
  employeeCount: number;
  driverCount: number;
  dispatcherCount: number;
  truckCount: number;
  createdAt: string;
}

export interface TenantDetails {
  // Company info
  id: number;
  name: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  taxId?: string;
  industry?: string;
  website?: string;
  mcDot?: string;
  serviceArea?: string;
  createdAt: string;

  // Boss info
  boss: {
    id: number;
    username: string;
    email: string;
    createdAt: string;
    isActive: boolean;
  } | null;

  // Subscription
  subscriptionId: number;
  subscriptionStatus: SubscriptionStatus;
  planId: number;
  planName: string;
  planSlug: PlanType;
  basePrice: number;
  billingCycle: 'monthly' | 'quarterly' | 'annual';
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  trialEndsAt?: string;
  credits: number;
  discountType?: 'percentage' | 'fixed';
  discountValue?: number;
  discountExpiresAt?: string;
  cancelledAt?: string;
  suspendedAt?: string;
  suspendReason?: string;

  // Plan limits
  maxTrucks?: number;
  maxDrivers?: number;
  maxDispatchers?: number;
  maxApiCallsPerMonth?: number;
  planFeatures?: Record<string, boolean>;

  // Employee counts
  employeeCounts: Record<string, number>;
}

export interface TenantEmployee {
  id: number;
  username: string;
  email: string;
  role: string;
  isActive: boolean;
  jobTitle?: string;
  createdAt: string;
}

export interface TenantOperations {
  ordersThisMonth: number;
  tasksThisMonth: number;
  tasksCompletedThisMonth: number;
  totalTrucks: number;
  activeDrivers: number;
}

export interface TenantFinancials {
  invoicesThisMonth: number;
  invoiceCountThisMonth: number;
  paidThisMonth: number;
}

export interface ApiUsageDay {
  date: string;
  totalCalls: number;
  avgResponseTime: number;
  errorCount: number;
}

export interface ApiEndpoint {
  method: string;
  path: string;
  callCount: number;
  avgResponseTime: number;
}

export interface TenantApiUsage {
  daily: ApiUsageDay[];
  topEndpoints: ApiEndpoint[];
}

export interface AuditLog {
  id: number;
  action: string;
  details: Record<string, unknown>;
  createdAt: string;
  adminUsername: string;
  adminEmail: string;
}

export interface DashboardStats {
  totalTenants: number;
  activeTenants: number;
  trialTenants: number;
  suspendedTenants: number;
  mrr: number;
  expiringTrials: number;
}

export interface RevenueChartPoint {
  month: string;
  mrr: number;
}

export interface SubscriptionPlan {
  id: number;
  name: string;
  slug: string;
  basePrice: number;
  billingCycle: string;
  maxTrucks?: number;
  maxDrivers?: number;
  maxDispatchers?: number;
  maxApiCallsPerMonth?: number;
  maxStorageGb?: number;
  features: Record<string, boolean>;
  isActive: boolean;
  sortOrder: number;
  subscriberCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface Announcement {
  id: number;
  title: string;
  content: string;
  type: 'info' | 'warning' | 'maintenance' | 'feature' | 'promotion';
  targetPlans: string[];
  isActive: boolean;
  startsAt?: string;
  expiresAt?: string;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreditTransaction {
  id: number;
  companyId: number;
  amount: number;
  type: 'add' | 'deduct' | 'apply' | 'refund';
  reason: string;
  balanceAfter: number;
  createdAt: string;
}

export interface SystemApiStats {
  daily: Array<{
    date: string;
    totalCalls: number;
    avgResponseTime: number;
    p95ResponseTime: number;
    errorCount: number;
    errorRate: number;
  }>;
  slowEndpoints: Array<{
    method: string;
    path: string;
    callCount: number;
    avgResponseTime: number;
    maxResponseTime: number;
  }>;
  topTenants: Array<{
    companyId: number;
    companyName: string;
    totalCalls: number;
    avgResponseTime: number;
  }>;
}

// ============ Shared UI Constants ============

export const SUBSCRIPTION_STATUS_COLORS: Record<string, string> = {
  active: 'green',
  trial: 'blue',
  suspended: 'red',
  cancelled: 'default',
  expired: 'orange',
};

export const PLAN_SLUG_COLORS: Record<string, string> = {
  starter: 'default',
  growth: 'purple',
  enterprise: 'geekblue',
};
